import React, { useState, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { tasksState, branchesState } from '../jotai/atoms'; 
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';
import './TaskComponent.css';

function TaskComponent({ onOpenModal, onTaskClick, onEditTask, onDeleteTask }) {
  const allTasks = useAtomValue(tasksState);
  const branches = useAtomValue(branchesState);
  const setTasks = useSetAtom(tasksState);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('단가');
  const [statusFilter, setStatusFilter] = useState('전체');
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [carryoverYear, setCarryoverYear] = useState(new Date().getFullYear());
  const [selectedYearFilterType, setSelectedYearFilterType] = useState('workYear');
  const [selectedHeadquartersFilter, setSelectedHeadquartersFilter] = useState('all');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');
  const [headquartersOptions, setHeadquartersOptions] = useState([]);
  const [branchMap, setBranchMap] = useState({});
  const [currentBranchOptions, setCurrentBranchOptions] = useState([]);
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedUnitPriceYear, setSelectedUnitPriceYear] = useState(currentYear.toString());
  const [selectedTotalPriceYear, setSelectedTotalPriceYear] = useState('all');
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false); // State for the new modal

  const years = ['all', ...Array.from({ length: 17 }, (_, i) => (currentYear - 11 + i).toString())];

  useEffect(() => {
    if (branches && branches.length > 0) {
      const uniqueHQs = ['all', ...new Set(branches.map(branch => branch.headquarters))];
      setHeadquartersOptions(uniqueHQs);

      const newBranchMap = branches.reduce((acc, branch) => {
        if (!acc[branch.headquarters]) {
          acc[branch.headquarters] = [];
        }
        acc[branch.headquarters].push(branch.name);
        return acc;
      }, {});
      setBranchMap(newBranchMap);
    }
  }, [branches]);

  useEffect(() => {
    if (selectedHeadquartersFilter !== 'all' && branchMap[selectedHeadquartersFilter]) {
      setCurrentBranchOptions(['all', ...branchMap[selectedHeadquartersFilter]]);
    } else {
      setCurrentBranchOptions([]);
    }
  }, [selectedHeadquartersFilter, branchMap]);

  const handleYearChange = (e) => {
    const year = e.target.value;
    if (activeTab === '단가') {
      setSelectedUnitPriceYear(year);
      if (showCheckboxes) {
        setCarryoverYear(year === 'all' ? currentYear : parseInt(year, 10));
      }
    } else {
      setSelectedTotalPriceYear(year);
      if (showCheckboxes) {
        setCarryoverYear(year === 'all' ? currentYear : parseInt(year, 10));
      }
    }
  };

  const handleIncrementYear = () => {
    setCarryoverYear(prevYear => prevYear + 1);
  };

  const handleDecrementYear = () => {
    setCarryoverYear(prevYear => prevYear - 1);
  };

  const handleTaskSelection = (taskId) => {
    setSelectedTasks(prevSelectedTasks => {
      if (prevSelectedTasks.includes(taskId)) {
        return prevSelectedTasks.filter(id => id !== taskId);
      } else {
        return [...prevSelectedTasks, taskId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allTaskIds = filteredTasks.map(task => task.id);
      setSelectedTasks(allTaskIds);
    } else {
      setSelectedTasks([]);
    }
  };

  const handleConfirmCarryover = async () => {
    try {
      await axiosInstance.put('/tasks/bulk-update-year', {
        taskIds: selectedTasks,
        newWorkYear: carryoverYear,
      });

      const response = await axiosInstance.get('/tasks');
      setTasks(response.data);

      setShowCheckboxes(false);
      setSelectedTasks([]);
      setCarryoverYear(new Date().getFullYear());
      Swal.fire({ icon: 'success', title: '성공', text: '공사 연도 이월이 완료되었습니다.' });
    } catch (error) {
      console.error('Error during year carryover:', error);
      Swal.fire({ icon: 'error', title: '오류', text: '공사 연도 이월 중 오류가 발생했습니다.' });
    }
  };

  const handleCancelCarryover = () => {
    setShowCheckboxes(false);
    setSelectedTasks([]);
    setCarryoverYear(new Date().getFullYear());
  };

  const filteredTasks = (allTasks || []).filter(task => {
    const matchesSearch = (
      String(task.displayNumber).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(task.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(task.number).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(task.mainTaskNumber).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(task.managers).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(task.assistants).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(task.period).toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(task.company).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.remarks && String(task.remarks).toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const matchesTab = task.type === activeTab;
    const currentSelectedYear = activeTab === '단가' ? selectedUnitPriceYear : selectedTotalPriceYear;
    let matchesYear = false;

    if (currentSelectedYear === 'all') {
      matchesYear = true;
    } else {
      if (selectedYearFilterType === 'workYear' && task.workYear && task.workYear.toString() === currentSelectedYear) {
        matchesYear = true;
      } else if (selectedYearFilterType === 'contractYear' && task.contractDate && new Date(task.contractDate).getFullYear().toString() === currentSelectedYear) {
        matchesYear = true;
      }
    }

    const matchesStatus = statusFilter === '전체' || 
      (statusFilter === '준공(완료보고기준)' ? task.completionReportStatus === '완료' : task.taskStatus === statusFilter);

    const matchesHeadquarters = selectedHeadquartersFilter === 'all' || task.headquarters === selectedHeadquartersFilter;
    const matchesBranch = selectedBranchFilter === 'all' || task.branch === selectedBranchFilter;
    
    return matchesSearch && matchesTab && matchesYear && matchesStatus && matchesHeadquarters && matchesBranch;
  }).sort((a, b) => {
    const numA = parseInt(a.displayNumber, 10);
    const numB = parseInt(b.displayNumber, 10);
    return numA - numB;
  });

  const totalContractAmount = filteredTasks.reduce((sum, task) => {
    const contractAmount = Number(task.initialContractAmount || 0) + Number(task.changeAmount || 0);
    return sum + contractAmount;
  }, 0);
  const totalCompletionAmount = filteredTasks.reduce((sum, task) => sum + Number(task.completionAmount || 0), 0);
  const totalRemainingAmount = totalContractAmount - totalCompletionAmount;

  const handleExcelDownload = async () => {
    try {
      const response = await axiosInstance.get(`/excel/tasks?type=${activeTab}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const today = new Date();
      const year = today.getFullYear().toString().slice(-2);
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const dateString = `${year}.${month}.${day}`;

      let filenamePrefix = activeTab === '단가' ? '단가공사리스트' : '총가공사리스트';
      const filename = `${filenamePrefix}_${dateString}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading Excel file:', error);
      Swal.fire({ icon: 'error', title: '오류', text: '엑셀 파일 다운로드 중 오류가 발생했습니다.' });
    }
  };

  const handleConstructionDetailsExcelDownload = async () => {
    try {
      const currentSelectedYear = activeTab === '단가' ? selectedUnitPriceYear : selectedTotalPriceYear;

      const params = new URLSearchParams({
        type: activeTab,
        year: currentSelectedYear,
        yearFilterType: selectedYearFilterType, // Use selected filter from UI
        status: statusFilter,
        headquarters: selectedHeadquartersFilter, // Use selected filter from UI
        branch: selectedBranchFilter, // Use selected filter from UI
        searchTerm: searchTerm,
      }).toString();

      const response = await axiosInstance.get(`/excel/construction-details?${params}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const today = new Date();
      const year = today.getFullYear().toString().slice(-2);
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const dateString = `${year}.${month}.${day}`;

      const filename = `공사내역_${dateString}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading Construction Details Excel file:', error);
      Swal.fire({ icon: 'error', title: '오류', text: '공사내역 엑셀 파일 다운로드 중 오류가 발생했습니다.' });
    }
  };

  return (
    <div style={{ paddingBottom: '60px' }}>
      <br /><br />
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
        <h2>공사 관리</h2>
        <select className="form-control" value={selectedHeadquartersFilter} onChange={(e) => {
          setSelectedHeadquartersFilter(e.target.value);
          setSelectedBranchFilter('all');
        }} style={{ width: '150px' }}>
          <option value="all">전체 본부</option>
          {headquartersOptions.filter(hq => hq !== 'all').map(hq => (
            <option key={hq} value={hq}>{hq}</option>
          ))}
        </select>
        <select
          className="form-control"
          value={selectedBranchFilter}
          onChange={(e) => setSelectedBranchFilter(e.target.value)}
          style={{ width: '150px' }}
          disabled={selectedHeadquartersFilter === 'all'}
        >
          <option value="all">전체 지사</option>
          {currentBranchOptions.filter(b => b !== 'all').map(b => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>
      <br /><br /><br />
      <div className="page-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <select
            className="year-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ marginRight: '10px' }}
          >
            <option value="전체">전체</option>
            <option value="공사중">공사중</option>
            <option value="준공">준공(전체)</option>
            <option value="준공(완료보고기준)">준공(완료보고기준)</option>
          </select>
          <input
            type="text"
            placeholder="공사 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="button-group">
          <label>
            <input
              type="checkbox"
              checked={selectedYearFilterType === 'workYear'}
              onChange={() => setSelectedYearFilterType('workYear')}
            />
            작업
          </label>
          <label style={{ marginLeft: '5px' }}>
            <input
              type="checkbox"
              checked={selectedYearFilterType === 'contractYear'}
              onChange={() => setSelectedYearFilterType('contractYear')}
            />
            계약
          </label>
          <select onChange={handleYearChange} value={activeTab === '단가' ? selectedUnitPriceYear : selectedTotalPriceYear} className="year-select" style={{ marginLeft: '10px' }}>
            <option value="all">전체</option>
            {years.filter(year => year !== 'all').map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
          {user && user.role === 'ADMIN' && (
            <button onClick={onOpenModal} className="register-button">
              신규 공사 등록하기
            </button>
          )}
          {user && ['ADMIN', 'MIDADMIN', 'USER'].includes(user.role) && (
            <button onClick={() => setIsExcelModalOpen(true)} className="register-button">
              엑셀 다운로드
            </button>
          )}
        </div>
      </div>
      <div className="tabs">
        <button
          className={`tab-button ${activeTab === '단가' ? 'active' : ''}`}
          onClick={() => setActiveTab('단가')}
        >
          단가
        </button>
        <button
          className={`tab-button ${activeTab === '총가' ? 'active' : ''}`}
          onClick={() => setActiveTab('총가')}
        >
          총가
        </button>
      </div>
      <div className="year-display" style={{ display: 'flex', alignItems: 'center' }}>
        {(activeTab === '단가' ? selectedUnitPriceYear : selectedTotalPriceYear) === 'all' ? '전체 공사 목록' : `${(activeTab === '단가' ? selectedUnitPriceYear : selectedTotalPriceYear)}년 공사 목록`}
        {user && ['ADMIN'].includes(user.role) && (
          <button onClick={() => {
            const newShowCheckboxes = !showCheckboxes;
            setShowCheckboxes(newShowCheckboxes);
            if (newShowCheckboxes) {
              const currentFilterYear = activeTab === '단가' ? selectedUnitPriceYear : selectedTotalPriceYear;
              setCarryoverYear(currentFilterYear === 'all' ? new Date().getFullYear() : parseInt(currentFilterYear, 10));
            }
          }} className="action-button" style={{ marginLeft: '10px', padding: '3px 8px' }}>
            {showCheckboxes ? '취소' : '연도이월'}
          </button>
        )}
      </div>
      <div className="task-count-display">
        Total: {filteredTasks.length} 건
      </div>
      {showCheckboxes && selectedTasks.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px', marginBottom: '10px' }}>
          <span style={{ marginRight: '10px', fontWeight: 'bold' }}>선택된 공사 이월 연도: {carryoverYear}년</span>
          <button onClick={handleDecrementYear} className="action-button" style={{ border: '1px solid red', color: 'red', background: 'none' }}>-</button>
          <button onClick={handleIncrementYear} className="action-button" style={{ marginLeft: '5px', border: '1px solid green', color: 'green', background: 'none' }}>+</button>
          <button onClick={handleConfirmCarryover} className="action-button" style={{ marginLeft: '30px', padding: '5px 10px' }}>
            확인
          </button>
          <button onClick={handleCancelCarryover} className="action-button" style={{ marginLeft: '5px', padding: '5px 10px' }}>
            취소
          </button>
        </div>
      )}
      <br />
      <table className="main-table task-list-table">
        <thead>
          <tr>
            {showCheckboxes && (
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={filteredTasks.length > 0 && selectedTasks.length === filteredTasks.length}
                />
              </th>
            )}
            <th>계약연도</th>
            {!showCheckboxes && <th>번호</th>}
            <th>공사명</th>
            <th>공사번호(감리)</th>
            <th>책임감리</th>
            <th>보조감리</th>
            <th>공기</th>
            <th>시공사</th>
            <th>계약금액</th>
            <th>준공금액(기성)</th>
            <th>잔액</th>
            {user && ['ADMIN', 'MIDADMIN'].includes(user.role) && <th>관리</th>}
          </tr>
        </thead>
        <tbody>
          {filteredTasks.map((task) => {
            const contractAmount = Number(task.initialContractAmount || 0) + Number(task.changeAmount || 0);
            const completionAmount = Number(task.completionAmount || 0);
            const balance = contractAmount - completionAmount;

            return (
              <tr key={task.id}
                className={task.taskStatus === '준공' ? 'completed-task' : ''}
                style={task.taskStatus === '준공' ? { backgroundColor: 'lightgreen' } : {}}>
                {showCheckboxes && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTasks.includes(task.id)}
                      onChange={() => handleTaskSelection(task.id)}
                      disabled={task.taskStatus === '준공'}
                    />
                  </td>
                )}
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{new Date(task.contractDate).getFullYear()}</td>
                {!showCheckboxes && <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{task.displayNumber}</td>}
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{task.name}</td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{task.number}</td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{task.managers}</td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{task.assistants}</td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>
                  {task.taskStatus === '준공' ? '준공' : task.period}
                </td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{task.company}</td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{contractAmount.toLocaleString()}</td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{completionAmount.toLocaleString()}</td>
                <td onClick={() => onTaskClick(task)} style={{ cursor: 'pointer' }}>{balance.toLocaleString()}</td>
                {user && ['ADMIN', 'MIDADMIN'].includes(user.role) && (
                  <td>
                    <div className="task-actions-container">
                      <button onClick={() => onEditTask(task)} className="task-edit-button">수정</button>
                      <button onClick={() => onDeleteTask(task.id)} className="task-delete-button">삭제</button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#f8f9fa',
        padding: '10px 20px',
        borderTop: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-around',
        fontWeight: 'bold',
        zIndex: 1000,
      }}>
        <div>총 계약금액: {totalContractAmount.toLocaleString()}원</div>
        <div>총 준공금액(기성): {totalCompletionAmount.toLocaleString()}원</div>
        <div>총 잔액: {totalRemainingAmount.toLocaleString()}원</div>
      </div>
      {isExcelModalOpen && (
        <div className="modal-overlay" onClick={() => setIsExcelModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-button" onClick={() => setIsExcelModalOpen(false)}>×</button>
            <h2 style={{ textAlign: 'center' }}>엑셀 다운로드</h2>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '30px' }}>
              <button onClick={() => {
                handleExcelDownload();
                setIsExcelModalOpen(false);
              }} className="register-button">
                공사관리
              </button>
              <button onClick={() => {
                handleConstructionDetailsExcelDownload();
                setIsExcelModalOpen(false);
              }} className="register-button">
                공사내역
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskComponent;